import _ from 'lodash';
import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { X, Info } from 'lucide-react';
import { Actions as CONSUMPTION_ACTIONS } from '../../store/actions/consumptionActions';
import { Actions as FACILITY_ACTIONS } from '../../store/actions/facilityActions';
import { Actions as REPORT_ACTIONS } from '../../store/actions/reportActions';
import { Button } from '../ui/button';
import { PeriodMaker } from '../utils/GlobalFunctions';
import { Alert, AlertDescription } from '../ui/alert';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import ReportOverview from './../HTMLReport/ReportOverview';
import ConsumptionEntity from './ConsumptionEntity';

const ConsumptionReport = ({
  report,
  getConsumption,
  entities,
  years,
  consumptionFilter,
  metrics,
  organisationDetails,
  getFacilityMetrics,
  getReport,
  toggleConsumptionFilter,
  branding,
  industries,
  popup,
  showConsumptionAlert,
}) => {
  const { headers } = branding.fonts?.[0].font || {};

  const options = ['m1', 'm2', 'm3', 'revenue', 'absolute'];

  const filterClasses = consumptionFilter.map((item) => `hide-${item}`);

  useEffect(() => {
    const reportId = window.location.pathname.split('/')[3];
    getReport(reportId);
  }, [getReport]);

  useEffect(() => {
    if (report && report.id) {
      getFacilityMetrics();
      getConsumption();
    }
    //eslint-disable-next-line
  }, [report && report.id]);

  const handleClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }

    showConsumptionAlert(false);
  };

  if (!report || !report.id) {
    <div className='row' style={{ fontFamily: headers }}>
      Loading...
    </div>;
  }

  let singleYear = false;
  if (years.length === 1) {
    singleYear = years[0];
  }

  return (
    <div className='row' style={{ fontFamily: headers }}>
      <style>
        {`.facility-values td:first-child { padding-left:40px; }`}
        {years?.map((year) => `.hide-year-${year} .toggle-year-${year} { display: none }`)}
        {options?.map((key) => `.hide-${key} .toggle-${key} { display: none }`)}
      </style>

      <div className='col-lg-12'>
        <section className='panel'>
          <ReportOverview mode='consumption' />
        </section>

        <div>
          {popup && (
            <div className='fixed top-[50px] right-4 z-50 w-[70%] md:w-[30%]'>
              <Alert
                className='flex items-center text-sm rounded-[10px] bg-[#FFD700] text-[#130834] border-[#FFD700]'
                onClick={handleClose}>
                <AlertDescription>
                  Please note that the consumption report is only available at a single organisational level.
                </AlertDescription>
                <button onClick={handleClose} className='ml-auto pl-2 text-[#130834] hover:opacity-70'>
                  <X size={16} strokeWidth={1.5} />
                </button>
              </Alert>
            </div>
          )}
        </div>

        <section className='panel'>
          <div className='panel-body' style={{ overflow: 'scroll' }}>
            <div className='clearfix'>
              <div className='flex justify-start md:justify-end gap-1 flex-wrap items-center'>
                <span>Show: </span>

                <Button variant='outline'
                  className={`btn ${consumptionFilter.includes('absolute') ? 'btn-default' : 'btn-success'}`}
                  onClick={() => toggleConsumptionFilter('absolute')}>
                  Absolute
                </Button>

                <Button variant='outline'
                  className={`btn ${consumptionFilter.includes('revenue') ? 'btn-default' : 'btn-success'}`}
                  onClick={() => toggleConsumptionFilter('revenue')}>
                  per million Revenue
                </Button>

                {organisationDetails &&
                  ['intensityRatioMetric1ID', 'intensityRatioMetric2ID', 'intensityRatioMetric3ID'].map((ir, index) => {
                    const orgIndustry = _.find(industries, { id: organisationDetails?.industry });
                    const metricID = orgIndustry?.[ir];
                    const metric = metrics && metrics.find((metric) => metric.id === metricID);
                    const metricName =
                      metric && (metric.name === 'Unit' ? orgIndustry.descriptionOfNonStandardRatio : metric.name);

                    return (
                      metric &&
                      metricID &&
                      metricID !== '230393' && (
                        <Button variant='outline'
                          key={crypto.randomUUID()}
                          className={`btn ${consumptionFilter.includes(`m${index + 1}`) ? 'btn-default' : 'btn-success'}`}
                          onClick={() => toggleConsumptionFilter(`m${index + 1}`)}>
                          per {metricName}
                        </Button>
                      )
                    );
                  })}
              </div>
            </div>
            <div className='clearfix'>
              <div style={{ float: 'right', marginTop: '10px' }}>
                <span>Show Year: </span>
                {years.map((year) => {
                  return (
                    <Button variant='outline'
                      key={crypto.randomUUID()}
                      className={`btn ${consumptionFilter.includes(`year-${year}`) ? 'btn-default' : 'btn-success'}`}
                      onClick={() => toggleConsumptionFilter(`year-${year}`)}>
                      {singleYear
                        ? PeriodMaker(report.startDate, report.endDate, singleYear)
                        : PeriodMaker(report.startDate, report.endDate, +year)}
                    </Button>
                  );
                })}
              </div>
            </div>
            <br />

            <h3 className='h3'>Consumption Disaggregated by Activity</h3>
            <table
              className={`app-table pie ${filterClasses.join(' ')}`}
              style={{ textAlign: 'right' }}>
              <thead>
                <tr>
                  <th width='20%'>
                    Category
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Info
                            size={14}
                            strokeWidth={1.5}
                            style={{ marginLeft: '0.5rem', color: branding?.colors?.primaryColor }}
                          />
                        </TooltipTrigger>
                        <TooltipContent>
                          <span style={{ fontSize: 12 }}>
                            Expenditure data has been converted into the currencies GBP (£) or USD ($) for the calculation of
                            tCO2e emissions.
                          </span>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </th>

                  {years.map((year) => {
                    return (
                      <th
                        key={crypto.randomUUID()}
                        className={`toggle-absolute toggle-year-${year}`}
                        width='15%'
                        style={{ textAlign: 'right', verticalAlign: 'middle' }}>
                        Absolute Consumption <br />
                        {singleYear
                          ? PeriodMaker(report.startDate, report.endDate, singleYear)
                          : PeriodMaker(report.startDate, report.endDate, +year)}
                      </th>
                    );
                  })}
                  {years.map((year) => {
                    return (
                      <th
                        key={crypto.randomUUID()}
                        className={`toggle-revenue toggle-year-${year}`}
                        width='15%'
                        style={{ textAlign: 'right', verticalAlign: 'middle' }}>
                        Normalised consumption per million revenue <br />
                        {singleYear
                          ? PeriodMaker(report.startDate, report.endDate, singleYear)
                          : PeriodMaker(report.startDate, report.endDate, +year)}
                      </th>
                    );
                  })}
                  {organisationDetails &&
                    ['intensityRatioMetric1ID', 'intensityRatioMetric2ID', 'intensityRatioMetric3ID'].map((ir, index) => {
                      const orgIndustry = _.find(industries, { id: organisationDetails?.industry });
                      const metricID = orgIndustry?.[ir];
                      const metric = metrics && metrics.find((metric) => metric.id === metricID);
                      const metricName =
                        metric && (metric.name === 'Unit' ? orgIndustry.descriptionOfNonStandardRatio : metric.name);

                      return (
                        metric &&
                        metricID &&
                        metricID !== '230393' &&
                        years.map((year) => {
                          return (
                            <th
                              key={crypto.randomUUID()}
                              className={`toggle-m${index + 1} toggle-year-${year}`}
                              width='15%'
                              style={{ textAlign: 'right', verticalAlign: 'middle' }}>
                              Normalised consumption per {metricName} <br />
                              {singleYear
                                ? PeriodMaker(report.startDate, report.endDate, singleYear)
                                : PeriodMaker(report.startDate, report.endDate, +year)}
                            </th>
                          );
                        })
                      );
                    })}
                </tr>
              </thead>

              <tbody>
                {entities?.map((entity) => (
                  <ConsumptionEntity entityID={entity.id} />
                ))}
              </tbody>
            </table>

            {/* <WasteRecyclable />
            <ElectricityRenewable /> */}
          </div>
        </section>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => {
  const {
    data,
    entities: consumptionEntities,
    subentities: consumptionSubentities,
    years,
    filter,
    wasteByType,
  } = state.consumption || {};

  const { subentities, entities, facility, organisation, report } = state;

  const neededEntities =
    entities &&
    consumptionEntities &&
    _.orderBy(
      entities.filter(({ id }) => consumptionEntities.includes(id)),
      ['name'],
    );
  const neededSubentities =
    subentities &&
    consumptionSubentities &&
    _.orderBy(
      subentities.filter(({ id }) => consumptionSubentities.includes(id)),
      ['name'],
    );

  const popup = state?.report?.showConsumptionAlert;

  return {
    report: report?.details,
    wasteByType,
    consumptionData: data,
    consumptionFilter: filter || [],
    subentities: neededSubentities,
    entities: neededEntities,
    years: years?.sort((a, b) => b - a) || [],
    facilities: facility && facility.list,
    facilityMetrics: facility?.metrics,
    metrics: state.metric?.list,
    types: state.metric?.types,
    organisationDetails: organisation?.details,
    organisationMetrics: organisation?.metrics,
    branding: state.branding,
    industries: organisation?.industries,
    popup,
  };
};

const mapDispatchToProps = (dispatch) => ({
  getReport: (id) => dispatch(REPORT_ACTIONS.getReport(id)),
  showConsumptionAlert: (show) => dispatch(REPORT_ACTIONS.showConsumptionAlert(show)),
  getFacilityMetrics: () => dispatch(FACILITY_ACTIONS.getFacilityMetrics()),
  getConsumption: () => dispatch(CONSUMPTION_ACTIONS.getConsumption()),
  toggleConsumptionFilter: (filter) => dispatch(CONSUMPTION_ACTIONS.toggleConsumptionFilter(filter)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ConsumptionReport);
